import { Identify, identify, setUserId, track } from '@amplitude/analytics-browser';
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';
import * as Sentry from '@sentry/nextjs';

import { BROWSER_AMPLITUDE_ID } from '../globals';
import { getErrorMessage } from './getErrorMessage';
import { getSavedSsrABTestResult } from './getSsrABTest';
import { HERO_COPIES_SSR_AB } from './SsrABTests';

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

interface EventClick {
  path?: { attributes: { name: string; value: string }[] }[];
}

export const trackPageView = (): void => {
  (window as any).ga('send', 'pageview', window.location.pathname);
};

export const trackEvent = async (event: EventClick): Promise<void> => {
  const analyticsEvent = getAnalyticsEvent(event);

  if (analyticsEvent) {
    const { category, action, label } = JSON.parse(analyticsEvent);
    // google
    (window as any).ga('send', 'event', category, action, label);

    //amplitude
    track(action, { category, label });
  }
};

const getAnalyticsEvent = (event: EventClick): string => {
  let analyticsEvent: string;

  event.path?.forEach(element => {
    if (!element.attributes) return;
    (Array.from(element.attributes) || []).forEach(attribute => {
      if (typeof analyticsEvent === 'undefined' && attribute.name === 'data-analytics-event') {
        analyticsEvent = attribute.value;
      }
    });
  });

  return analyticsEvent;
};

export function trackDownloadClick(): void {
  try {
    trackGoogleTagManagerEvent({ event: 'clickedToAppStore' });
  } catch (error) {
    Sentry.captureException(
      'trackDownloadClick(): failed to track download click in google tag manager',
      {
        extra: { reason: error },
      },
    );
  }
}

export async function trackSignUp(params: {
  email: string;
  event: 'sign_up_email' | 'sign_up_apple' | 'sign_up_facebook' | 'sign_up_google';
  fbEventId: string;
  userId: string;
}): Promise<void> {
  try {
    // deprecated 'signUp', okay to remove february 17, 2023
    trackGoogleTagManagerEvent({ event: 'signUp', transactionId: params.userId });
    trackGoogleTagManagerEvent({ event: 'createdAccount', transactionId: params.userId });
  } catch (error) {
    Sentry.captureException('trackSignUpEvent(): failed to track signup in google tag manager', {
      extra: { reason: error },
    });
  }

  try {
    // https://stackoverflow.com/questions/65974337/import-es-module-in-next-js-err-require-esm
    const { changeUser, getUser, logCustomEvent, requestImmediateDataFlush } = await import(
      '@braze/web-sdk'
    );
    changeUser(params.userId);
    getUser().setEmail(params.email);
    logCustomEvent(params.event);
    requestImmediateDataFlush();
  } catch (error) {
    Sentry.captureException('trackSignUpEvent(): failed to track signup in braze', {
      extra: { reason: error },
    });
  }

  try {
    setUserId(params.userId);
    identify(new Identify().set('email', params.email));
  } catch (error) {
    Sentry.captureException('trackSignUpEvent(): failed to sign up in amplitude', {
      extra: { reason: error },
    });
  }

  try {
    const hashedUserId = await getSHA256Hash(params.userId);

    // react-facebook-pixel can only be imported in the browser, as it crashes
    // server-side rendering
    const { default: ReactPixel } = await import('react-facebook-pixel')
    ReactPixel.track('CompleteRegistration', {
      eventID: params.fbEventId,
      external_id: hashedUserId,
    });
  } catch (error) {
    Sentry.captureException('trackSignUpEvent(): failed to track signup in facebook', {
      extra: { reason: error },
    });
  }
}

// copied over from web-app
function trackGoogleTagManagerEvent(params: {
  event: 'clickedToAppStore' | 'createdAccount' | 'signUp';
  transactionId?: string;
  conversionValue?: number;
}): void {
  (global.window as unknown as WindowWithGoogleTagManager).dataLayer.push(params);
}

type WindowWithGoogleTagManager = {
  dataLayer: Datalayer;
};

type Datalayer = {
  push: (event: { event: string; transactionId?: string; conversionValue?: number }) => boolean;
};

async function logEventWithProperties(
  event: string,
  properties: { [key: string]: unknown },
): Promise<void> {
  try {
    const { logCustomEvent } = await import('@braze/web-sdk');
    logCustomEvent(event, properties);
  } catch (error) {
    Sentry.captureException('logEventWithProperties(): failed to log in braze', {
      extra: { reason: error },
    });
  }

  try {
    await track(event, properties).promise;
  } catch (error) {
    Sentry.captureException('logEventWithProperties(): failed to log in amplitude', {
      extra: { reason: error },
    });
  }
}

export async function trackSignUpAttempt(
  method: 'email' | 'facebook' | 'apple' | 'google',
): Promise<void> {
  await logEventWithProperties('core_sign_up_attempt', {
    core_sign_up_method: method,
    core_sign_up_location: 'marketing',
  });
}
export async function trackSignUpError(
  method: 'email' | 'facebook' | 'apple' | 'google',
  error: unknown,
): Promise<void> {
  await logEventWithProperties('core_sign_up_error', {
    core_error_message: getErrorMessage(error),
    core_sign_up_method: method,
    core_sign_up_location: 'marketing',
  });
}
export async function trackSignUpSuccess(
  method: 'email' | 'facebook' | 'apple' | 'google',
): Promise<void> {
  await logEventWithProperties('core_sign_up_success', {
    core_sign_up_method: method,
    core_sign_up_location: 'marketing',
    [HERO_COPIES_SSR_AB.key]: getSavedSsrABTestResult(HERO_COPIES_SSR_AB),
  });
}

export enum AmplitudeExperimentClientStatuses {
  Uninitialized = 'Uninitialized',
  Initializing = 'Initializing',
  Initialized = 'Initialized',
  Errored = 'Errored',
}

export enum AmplitudeExperimentInitializedResult {
  Success = 'Success',
  Error = 'Error',
  TimedOut = 'TimedOut',
}

// Add a listeners system to track status changes
const statusListeners: Set<(status: AmplitudeExperimentClientStatuses) => void> = new Set();

type AmplitudeExperimentClientRef = {
  client: ExperimentClient | null;
  flags: {
    [AMPLITUDE_EXPERIMENT_IDS.CTA_TEST]?: {
      value?: string;
      payload?: string;
    };
    [AMPLITUDE_EXPERIMENT_IDS.SIGN_UP_MODAL_CTA]?: {
      value?: string;
      payload?: string;
    };
    [AMPLITUDE_EXPERIMENT_IDS.UPDATED_PRICING]?: {
      value?: string;
    };
  };
  status: AmplitudeExperimentClientStatuses;
  setStatus: (newStatus: AmplitudeExperimentClientStatuses) => void;
};

export const AMPLITUDE_EXPERIMENT_IDS = {
  DOWNLOAD_MAC_APP_TEST: 'marketing-mac-app-download',
  CTA_TEST: 'marketing-cta-test',
  SIGN_UP_MODAL_CTA: 'marketing-sign-up-modal-cta',
  UPDATED_PRICING: 'all-updated-pricing',
} as const;


export const AMPLITUDE_EXPERIMENT: AmplitudeExperimentClientRef = {
  client: null,
  flags: {},
  status: AmplitudeExperimentClientStatuses.Uninitialized,
  setStatus: newStatus => {
    AMPLITUDE_EXPERIMENT.status = newStatus;
    statusListeners.forEach(listener => listener(newStatus));
  },
};

export async function initializeAmplitudeExperiments(): Promise<void> {
  try {
    AMPLITUDE_EXPERIMENT.setStatus(AmplitudeExperimentClientStatuses.Initializing);
    const client = Experiment.initializeWithAmplitudeAnalytics(BROWSER_AMPLITUDE_ID);
    await client.start();
    AMPLITUDE_EXPERIMENT.client = client;
    AMPLITUDE_EXPERIMENT.setStatus(AmplitudeExperimentClientStatuses.Initialized);

    AMPLITUDE_EXPERIMENT.flags[AMPLITUDE_EXPERIMENT_IDS.CTA_TEST] = AMPLITUDE_EXPERIMENT.client?.variant(AMPLITUDE_EXPERIMENT_IDS.CTA_TEST) || {};
    AMPLITUDE_EXPERIMENT.flags[AMPLITUDE_EXPERIMENT_IDS.SIGN_UP_MODAL_CTA] = AMPLITUDE_EXPERIMENT.client?.variant(AMPLITUDE_EXPERIMENT_IDS.SIGN_UP_MODAL_CTA) || {};
    AMPLITUDE_EXPERIMENT.flags[AMPLITUDE_EXPERIMENT_IDS.UPDATED_PRICING] = AMPLITUDE_EXPERIMENT.client?.variant(AMPLITUDE_EXPERIMENT_IDS.UPDATED_PRICING) || {};
  } catch (error) {
    AMPLITUDE_EXPERIMENT.setStatus(AmplitudeExperimentClientStatuses.Errored);
    console.error('Failed to initialize Amplitude Experiment client', { reason: error });
  }
}

export function getAmplitudeExperimentsStatus(): AmplitudeExperimentClientStatuses {
  return AMPLITUDE_EXPERIMENT.status;
}

export function getCTATextFeatureFlagPayload(): string | undefined {
  return AMPLITUDE_EXPERIMENT.flags[AMPLITUDE_EXPERIMENT_IDS.CTA_TEST]?.payload as string | undefined;
}

export function getSignUpModalCTATextFeatureFlagPayload(): string | undefined {
  return AMPLITUDE_EXPERIMENT.flags[AMPLITUDE_EXPERIMENT_IDS.SIGN_UP_MODAL_CTA]?.payload as string | undefined;
}

export function getUpdatedPricingFeatureFlag(): 'control' | 'modest-increase' | undefined {
  return AMPLITUDE_EXPERIMENT.flags[AMPLITUDE_EXPERIMENT_IDS.UPDATED_PRICING]?.value as 'control' | 'modest-increase' | undefined;
}

export async function waitForAmplitudeExperimentInitialized(timeoutMs: number = 10000): Promise<AmplitudeExperimentInitializedResult> {
  const startTime = Date.now();
  let status = getAmplitudeExperimentsStatus();

  while (
    status === AmplitudeExperimentClientStatuses.Uninitialized ||
    status === AmplitudeExperimentClientStatuses.Initializing
  ) {
    if (Date.now() - startTime > timeoutMs) {
      return AmplitudeExperimentInitializedResult.TimedOut;
    }

    await new Promise(resolve => setTimeout(resolve, 200));
    status = getAmplitudeExperimentsStatus();
  }

  if (status === AmplitudeExperimentClientStatuses.Errored) {
    return AmplitudeExperimentInitializedResult.Error;
  }

  return AmplitudeExperimentInitializedResult.Success;
}


export async function getSHA256Hash(input?: string | null): Promise<string> {
  if (!input) return '';

  try {
    const textAsBuffer = new TextEncoder().encode(input);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hash = hashArray.map(item => item.toString(16).padStart(2, '0')).join('');
    return hash;
  } catch (error) {
    return input;
  }
}

export function createFacebookEventId() {
  try {
    return `${window.crypto.randomUUID()}-${Date.now()}`;
  } catch (error) {
    return `${Math.random().toString(36)}-${Date.now()}`;
  }
}
